'use client'

import { supabase } from '@/supabase'
import { Database } from '@/supabase/database.types'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useSession } from './session'

export type Role = Database['public']['Enums']['role_key']

export type RoleState =
  | {
      type: 'LOADING'
    }
  | {
      type: 'IDLE'
      roles: Role[]
    }

export type PermissionResult = 'LOADING' | 'NOT_PERMITTED' | 'PERMITTED'

type Context = {
  state: RoleState
  update: () => Promise<void>
}

const RoleContext = createContext<Context>({
  state: { type: 'LOADING' },
  update: async () => {},
})

export const RoleProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [data, setData] = useState<RoleState>({
    type: 'LOADING',
  })
  const session = useSession()

  const setup = async () => {
    try {
      if (session.state.type !== 'LOGGED_IN') {
        setData({ type: 'LOADING' })
        return
      }

      const result = await supabase
        .from('users')
        .select('roles(key)')
        .eq('uuid', session.state.data.user.id)
        .single()

      if (result.error !== null) {
        console.error('fetch user roles failed', result.error)
        setData({ type: 'IDLE', roles: [] })
        return
      }

      const roles = result.data.roles?.map(role => role.key) || []
      setData({ type: 'IDLE', roles })
    } catch (error) {
      console.error('fetch user roles failed', error)
      setData({ type: 'IDLE', roles: [] })
    }
  }

  useEffect(() => {
    setup()
    const interval = setInterval(setup, 1000 * 60 * 10)
    return () => {
      clearInterval(interval)
    }
  }, [session.state])

  const contextValue = useMemo(() => {
    return { state: data, update: setup }
  }, [data])

  return (
    <RoleContext.Provider value={contextValue}>{children}</RoleContext.Provider>
  )
}

export const hasRole = (...roles: Role[]) => {
  const roleState = useContext(RoleContext)
  return useMemo<PermissionResult>(() => {
    if (roleState === null) return 'NOT_PERMITTED'
    if (roleState.state.type === 'LOADING') return 'LOADING'
    const roleSet = new Set(roleState.state.roles)
    for (const role of roles) {
      if (roleSet.has(role) === false) {
        return 'NOT_PERMITTED'
      }
    }
    return 'PERMITTED'
  }, [roleState])
}

export const useRole = () => {
  return useContext(RoleContext)
}
